import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { App as CapApp } from '@capacitor/app';
import { ServicesProvider, type AppServices } from './services';
import { AppRoutes } from './AppRoutes';
import { ToastProvider } from '../ui';
import { decideBack, dispatchScreenBack } from './backButton';
import { SyncProvider } from '../sync/SyncProvider';
import { PremiumProvider } from '../premium/PremiumProvider';
import { formatInviteCode, parseInviteCode } from '../sync/sharing';
import { useSharedDocuments } from '../features/import/shareIn';

/**
 * Android hardware back button; see decideBack for the rules.
 * Registered exactly once: useNavigate() returns a new function on every
 * route change, and re-registering on each change briefly left two
 * listeners alive (removal is async), so one press went back twice.
 */
function useAndroidBackButton(enabled: boolean) {
  const navigate = useNavigate();
  const location = useLocation();
  const latest = useRef({ navigate, pathname: location.pathname });
  latest.current = { navigate, pathname: location.pathname };

  useEffect(() => {
    if (!enabled) return;
    const handle = CapApp.addListener('backButton', ({ canGoBack: webViewCanGoBack }) => {
      const { navigate: go, pathname } = latest.current;
      // The WebView's own history does not count pushState navigations, so
      // Capacitor reports canGoBack=false inside the SPA. The router keeps
      // its position in history.state.idx; trust that first.
      const routerIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;
      const canGoBack = routerIndex > 0 || webViewCanGoBack;
      switch (decideBack({ pathname, canGoBack, handledByScreen: dispatchScreenBack() })) {
        case 'handled':
          break;
        case 'history-back':
          window.history.back();
          break;
        case 'go-home':
          go('/', { replace: true });
          break;
        case 'exit':
          void CapApp.exitApp();
          break;
      }
    });
    return () => {
      void handle.then((h) => h.remove());
    };
  }, [enabled]);
}

/**
 * An invite link opened on a phone that has the app (once the web address
 * is linked to the app): the app opens on the join screen with the code.
 */
function useInviteLinks(enabled: boolean) {
  const navigate = useNavigate();
  const go = useRef(navigate);
  go.current = navigate;
  useEffect(() => {
    if (!enabled) return;
    const handle = CapApp.addListener('appUrlOpen', ({ url }) => {
      const code = parseInviteCode(url);
      if (code && /\/join\//.test(url)) go.current(`/join/${formatInviteCode(code)}`);
    });
    return () => {
      void handle.then((h) => h.remove());
    };
  }, [enabled]);
}

/** Everything below the router. main.tsx supplies BrowserRouter, tests MemoryRouter. */
export function AppRoot({ services }: { services: AppServices }) {
  useAndroidBackButton(services.platform === 'android');
  useInviteLinks(services.platform !== 'web');
  useSharedDocuments(services.platform);
  return (
    <ServicesProvider services={services}>
      <PremiumProvider>
        <SyncProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </SyncProvider>
      </PremiumProvider>
    </ServicesProvider>
  );
}
